import { OptionModel } from '@/shared/lib/types'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import clsx from 'clsx'
import { JSX, ReactElement } from 'react'

type BaseTabsProps<T extends string | number> = {
  tabs: OptionModel[]
  selectedTab: T
  onSelect: (tab: T) => void
  iconComponent?: (name: T) => JSX.Element
  direction?: 'col' | 'row'
  variant?: 'primary' | 'secondary' | 'filter'
  className?: string
  tabPanels?: ReactElement[]
}

export const BaseTabs = <T extends string | number>({
  tabs,
  selectedTab,
  onSelect,
  iconComponent,
  className,
  direction = 'col',
  variant = 'primary',
  tabPanels,
}: BaseTabsProps<T>) => {
  const selectedIndex = tabs.find((t) => t.value === selectedTab)?.index

  const tabsPosition = direction === 'col' ? 'flex flex-col gap-1' : 'flex flex-row gap-[10px]'
  const variantClasses =
    variant === 'primary'
      ? 'w-full flex gap-2 text-text-secondary dark:text-primary-dark data-[selected]:bg-light-purple dark:data-[selected]:bg-dark-purple data-[selected]:text-primary dark:data-[selected]:text-primary-dark data-[hover]:bg-light-purple dark:data-[hover]:bg-dark-purple data-[hover]:text-primary dark:data-[hover]:text-primary-dark py-2 px-5 rounded-lg duration-100 focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-dark focus:ring-offset-0'
      : variant === 'secondary'
        ? 'flex gap-2 text-text-secondary data-[selected]:border-b data-[selected]:border-primary dark:data-[selected]:border-primary-dark dark:data-[selected]:text-primary-dark data-[selected]:text-primary data-[hover]:text-primary dark:data-[hover]:text-primary-dark py-1 pr-1 duration-100 focus:outline-none'
        : 'focus:outline-none focus-visible:outline-0 disabled:cursor-not-allowed disabled:opacity-75 aria-disabled:cursor-not-allowed aria-disabled:opacity-75 flex-shrink-0 focus-visible:ring-2 focus-visible:ring-primary rounded-lg data-[hover]:ring-1 data-[selected]:text-primary data-[selected]:ring-1 data-[selected]:ring-inset px-4 py-2 text-text-secondary disabled:bg-transparent aria-disabled:bg-transparent dark:disabled:bg-transparent dark:aria-disabled:bg-transparent'

  return (
    <TabGroup vertical selectedIndex={selectedIndex ?? 0}>
      <TabList
        className={clsx(
          tabsPosition,
          'overflow-x-auto md:overflow-x-visible whitespace-nowrap md:whitespace-normal scrollbar-hide'
        )}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.index}
            className={`${variantClasses} ${className}`}
            onClick={() => onSelect(tab.value as T)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                onSelect(tab.value as T)
              }
            }}
          >
            {iconComponent && iconComponent(tab.value as T)}
            <span>{tab.label}</span>
          </Tab>
        ))}
      </TabList>
      <TabPanels>{tabPanels?.map((tp, i) => <TabPanel key={i}>{tp}</TabPanel>)}</TabPanels>
    </TabGroup>
  )
}
